<template>
  <InsideContent
    :title="$t('APP.IAB.PRODUCTS_TITLE')"
    :hint="$t('HINTS.APPS.PAYMENT.PRODUCTS')"
  >
    <div class="flex-container align-middle align-justify">
      <ElButton
        icon="el-icon-plus"
        type="primary"
        :loading="isCreatingProduct"
        @click="openProductCreateModal"
      >
        {{ $t('APP.IAB.NEW_PRODUCT_BTN') }}
      </ElButton>

      <div class="shrink">
        <ElSelect v-model="typeFilter">
          <ElOption :label="$t('ALL')" value="all" />
          <ElOption
            :label="$t('APP.IAB.PRODUCT_TYPES.SUBSCRIPTION')"
            value="subscription"
          />
          <ElOption :label="$t('APP.IAB.PURCHASABLE')" value="purchasable" />
        </ElSelect>
      </div>
    </div>

    <CardApiError
      v-if="apiError"
      :error-text="$t('APP.IAB.API_ERROR')"
      @retry="getInfo"
    />

    <InAppPurchaseProductsTable
      v-else
      v-loading="isFetching"
      :products="filteredProducts"
      :is-raised="false"
      @edit-product="updateProduct"
    />
  </InsideContent>
</template>

<script>
import { format as priceFormat, label as priceLabel } from '@/utils/number';
import InsideContent from '@@/common/layout/InsideContent';
import InAppPurchaseProductsTable from '@@/app/common/in-app-purchase/InAppPurchaseProductsTable';
import InAppPurchaseProductModal from '@@/app/common/in-app-purchase/InAppPurchaseProductModal';
import CardApiError from '@@/common/CardApiError';
import { Products } from '@/api/api';
import getKindLabel from '@/utils/apps/kindLabel';

export default {
  name: 'InAppPurchaseProducts',

  components: {
    InsideContent,
    CardApiError,
    InAppPurchaseProductsTable,
  },

  props: {
    packageName: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      typeFilter: 'all',
      products: [],
      isFetching: true,
      isCreatingProduct: false,
      apiError: false,
    };
  },

  computed: {
    filteredProducts() {
      switch (this.typeFilter) {
        case 'all': {
          return this.products;
        }
        case 'purchasable': {
          return this.products.filter(p => p.kind === 'P');
        }
        default: {
          return this.products.filter(p => p.kind !== 'P');
        }
      }
    },

    hasAnySubscriptionProduct() {
      return Boolean(this.products.filter(p => p.kind !== 'P').length);
    },
  },

  created() {
    this.getInfo();
  },

  methods: {
    priceLabel,
    priceFormat,

    getInfo() {
      this.apiError = false;
      this.isFetching = true;

      return Products.get({ packageName: this.packageName })
        .then(({ body: { products = [] } }) => {
          this.products = [...products].sort(a => (a.kind === 'R' ? -1 : +1));
        })
        .catch(({ body: { message } = {} }) => {
          if (message) {
            this.$message.error(message);
          }

          this.apiError = true;
        })
        .finally(() => {
          this.isFetching = false;
        });
    },

    getStateLabel(state) {
      if (state === 'A') {
        return this.$t('ACTIVE');
      }

      if (state === 'D') {
        return this.$t('DISABLED');
      }

      return '';
    },

    openProductCreateModal() {
      this.isCreatingProduct = true;

      return this.$modal
        .open({
          component: InAppPurchaseProductModal,
          title: this.$t('APP.IAB.ADD_PRODUCT'),
          props: {
            confirmButtonText: this.$t('APP.IAB.ADD_PRODUCT_BTN'),
          },
          width: '40%',
        })
        .then(newProduct => {
          if (!newProduct) {
            this.isCreatingProduct = false;
            return;
          }

          this.createProduct(newProduct);
        });
    },

    createProduct(newProduct) {
      this.isCreatingProduct = true;

      return Products.save({ packageName: this.packageName }, newProduct)
        .then(({ body: { product = {} } }) => {
          this.addProductToList(product);
        })
        .catch(({ body: { message } = {} }) => {
          if (message) {
            this.$message.error(message);
          }
        })
        .finally(() => {
          this.isCreatingProduct = false;
        });
    },

    updateProduct(updatedProduct) {
      const { sku, ...productData } = updatedProduct;
      return Products.update(
        {
          sku,
          packageName: this.packageName,
        },
        productData,
      )
        .then(({ body: { product = {} } }) => {
          this.updateProductInList({ ...product, sku });
        })
        .catch(({ body: { message } = {} }) => {
          if (message) {
            this.$message.error(message);
          }
        });
    },

    addProductToList(product) {
      this.products.push(product);
    },

    updateProductInList(updatedProduct) {
      this.products = this.products.map(product => {
        if (product.sku === updatedProduct.sku) return updatedProduct;
        return product;
      });
    },

    translateKindLabel(kind) {
      if (kind === 'R') {
        return this.$t('APP.IAB.TRIAL_SUBSCRIPTION');
      }

      return this.$t(getKindLabel(kind));
    },
  },
};
</script>
